import React, { useState, useEffect } from 'react';

import { Button, Table, Segment, Container, Message } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import { multiCaseGet } from 'libs/format';
import { selectBusinessLocations } from 'selectors';

import { getLocationOptions } from './AccountLocationList.utils';
import LocationRow from './components/LocationRow';

import './AccountLocationList.scss';

function AccountLocationList({ gmbLocations, gmbAccountId, onDisconnect, onLocationsAssociation }) {
  const fishermanLocations = useSelector((state) =>
    selectBusinessLocations(state, { camelCase: true }),
  );

  const [gmbLocationMap, setGmbLocationMap] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  function onFishermanLocationSelect(gmbLocationId, fishermanLocationId) {
    if (fishermanLocationId !== null) {
      const isAlreadyMapped = Object.entries(gmbLocationMap).some(
        ([otherGmbLocationId, mappedFishermanLocationId]) =>
          otherGmbLocationId !== gmbLocationId && mappedFishermanLocationId === fishermanLocationId,
      );

      if (isAlreadyMapped) {
        return;
      }
    }

    const newIsDirty =
      fishermanLocationId !== null ||
      Object.keys(gmbLocationMap).reduce(
        (accumIsDirty, key) =>
          (key !== gmbLocationId && gmbLocationMap[key] !== null) || accumIsDirty,
        false,
      );

    setGmbLocationMap({
      ...gmbLocationMap,
      [gmbLocationId]: fishermanLocationId,
    });
    setIsDirty(newIsDirty);
  }

  function onSubmit() {
    const data = Object.keys(gmbLocationMap)
      .map((gmbLocationId) => {
        return {
          gmbLocationId,
          gmbAccountId,
          fishermanLocationId: gmbLocationMap[gmbLocationId],
        };
      })
      .filter((locationMapping) => multiCaseGet(locationMapping, 'fishermanLocationId') !== null);

    onLocationsAssociation(data);
  }

  function getSelectableLocations(gmbLocation) {
    // Given a gmbLocation it returns valid location options for a select component
    const gmbLocationId = multiCaseGet(gmbLocation, 'locationId');

    // Get all fisherman location IDs that are already mapped to OTHER GMB locations
    const pairedLocationIds = new Set();
    Object.entries(gmbLocationMap).forEach(([otherGmbLocationId, fishermanLocationId]) => {
      // Only exclude fisherman locations that are mapped to OTHER GMB locations
      if (otherGmbLocationId !== gmbLocationId && fishermanLocationId !== null) {
        pairedLocationIds.add(fishermanLocationId);
      }
    });

    const availableLocations = fishermanLocations.filter(
      (location) => !pairedLocationIds.has(location.id),
    );
    return getLocationOptions(availableLocations);
  }

  function attemptLocationMapping() {
    if (!Array.isArray(gmbLocations)) {
      return;
    }

    gmbLocations.forEach((gmbLocation) => {
      const match = fishermanLocations.find((fishermanLocation) => {
        const street = multiCaseGet(gmbLocation, 'storefrontAddress.addressLines.0');
        const locality = multiCaseGet(gmbLocation, 'storefrontAddress.locality');
        const postalCode = multiCaseGet(gmbLocation, 'storefrontAddress.postalCode');

        return (
          fishermanLocation.city === locality &&
          fishermanLocation.street === street &&
          fishermanLocation.zipCode === postalCode
        );
      });

      if (match) {
        const gmbLocationId = multiCaseGet(gmbLocation, 'locationId');
        onFishermanLocationSelect(gmbLocationId, match.id);
      }
    });
  }

  useEffect(() => {
    attemptLocationMapping();
  }, [gmbLocations]);

  if (gmbLocations === null) {
    return <Segment attached="bottom" padded="very" loading />;
  }

  if (_isEmpty(gmbLocations)) {
    return (
      <Segment attached="bottom" className="account-location-list">
        <Message negative>
          <Message.Header>No Google Business Profile locations associated</Message.Header>
          The selected Google account does not have any Google Business Profile locations
          associated. Please disconnect and try another Google account.
        </Message>

        <Container textAlign="right">
          <Button negative onClick={onDisconnect}>
            Disconnect
          </Button>
        </Container>
      </Segment>
    );
  }

  return (
    <Segment attached="bottom" className="account-location-list">
      <Table singleLine>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Business Location</Table.HeaderCell>
            <Table.HeaderCell>Status & Details</Table.HeaderCell>
            <Table.HeaderCell>Confirm Location</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {gmbLocations.map((gmbLocation) => (
            <LocationRow
              key={multiCaseGet(gmbLocation, 'locationId')}
              gmbLocation={gmbLocation}
              gmbLocationMap={gmbLocationMap}
              onFishermanLocationSelect={onFishermanLocationSelect}
              getSelectableLocations={getSelectableLocations}
            />
          ))}
        </Table.Body>
      </Table>
      <Container textAlign="right">
        <Button disabled={!isDirty} onClick={onSubmit} primary>
          Confirm
        </Button>
      </Container>
    </Segment>
  );
}

export default AccountLocationList;
